import { useEffect, useRef, useState, useCallback } from 'react';

export interface AliyunCaptchaConfig {
  enabled: boolean;
  sceneId: string;
  prefix: string;
  region: string;
}

/* ---------- constants ---------- */

const CAPTCHA_ELEMENT_ID = 'captcha-element';
const CAPTCHA_BUTTON_ID = 'captcha-button';

/* ---------- global type for dynamically loaded SDK ---------- */

declare global {
  interface Window {
    AliyunCaptchaConfig?: { region: string; prefix: string };
  }

  function initAliyunCaptcha(config: {
    SceneId: string;
    prefix: string;
    mode: string;
    element: string;
    button: string;
    captchaVerifyCallback: (captchaVerifyParam: string) => Promise<{ captchaResult: boolean; bizResult: boolean }>;
    onBizResultCallback: (bizResult: boolean) => void;
    getInstance?: (instance: unknown) => void;
    slideStyle?: { width: number; height: number };
    language?: string;
  }): Promise<void>;
}

/* ---------- script loader (cached) ---------- */

const SCRIPT_URL = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js';

let scriptPromise: Promise<void> | null = null;

function loadCaptchaScript(region: string, prefix: string): Promise<void> {
  // V3 requires global config BEFORE the script loads
  window.AliyunCaptchaConfig = { region, prefix };

  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null; // allow retry on next mount
      reject(new Error('Captcha script load failed'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/* ---------- hook ---------- */

/**
 * Loads the Aliyun Captcha 2.0 SDK and initialises a popup-mode instance.
 *
 * When `config.enabled` is false the hook is inert — `ready` stays false,
 * no script is loaded, and `trigger` is a no-op. The login page can then
 * fall through to a direct password login.
 *
 * @param config  Captcha parameters from environment variables.
 * @param onVerified  Called with the raw `captchaVerifyParam` string once the
 *                    user completes the slider challenge.
 */
export function useAliyunCaptcha(
  config: AliyunCaptchaConfig,
  onVerified: (captchaVerifyParam: string) => void,
) {
  const onVerifiedRef = useRef(onVerified);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep callback ref in sync without re-running the effect.
  onVerifiedRef.current = onVerified;

  useEffect(() => {
    if (!config.enabled) return;

    if (!config.sceneId || !config.prefix || !config.region) {
      setError('验证组件配置不完整');
      return;
    }

    let cancelled = false;

    loadCaptchaScript(config.region, config.prefix)
      .then(async () => {
        if (cancelled) return;

        // Ensure DOM mount-points exist before init
        const el = document.getElementById(CAPTCHA_ELEMENT_ID);
        const btn = document.getElementById(CAPTCHA_BUTTON_ID);
        if (!el || !btn) return;

        try {
          await initAliyunCaptcha({
            SceneId: config.sceneId,
            prefix: config.prefix,
            mode: 'popup',
            element: `#${CAPTCHA_ELEMENT_ID}`,
            button: `#${CAPTCHA_BUTTON_ID}`,
            captchaVerifyCallback: async (captchaVerifyParam: string) => {
              onVerifiedRef.current(captchaVerifyParam);
              return { captchaResult: true, bizResult: true };
            },
            onBizResultCallback: () => {},
            slideStyle: { width: 300, height: 40 },
            language: 'cn',
          });
          if (!cancelled) setReady(true);
        } catch {
          if (!cancelled) setError('验证组件初始化失败，请刷新页面重试');
        }
      })
      .catch(() => {
        if (!cancelled) setError('验证组件加载失败，请刷新页面重试');
      });

    return () => {
      cancelled = true;
    };
  }, [config.enabled, config.sceneId, config.prefix, config.region]);

  const trigger = useCallback(() => {
    document.getElementById(CAPTCHA_BUTTON_ID)?.click();
  }, []);

  return { ready, error, trigger, captchaElementId: CAPTCHA_ELEMENT_ID, captchaButtonId: CAPTCHA_BUTTON_ID };
}
